import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AuthenticatedLayout from './Components/Layout/AuthenticatedLayout';
import FlightRangeAdmin from './views/FlightRangeAdmin';
import FlightRangeModify from './views/FlightRangeModify';
import FlightRange from './views/FlightRange';
import Home from './Components/Home/Home';
import { useAuth } from 'react-oidc-context';
import ProtectedRoute from './Components/Routes/ProtectedRoute';
import { useUserRole } from './hooks/useUserRole';
import './App.css';

const App: React.FC = () => {
  const auth = useAuth();
  const { isAuthenticated } = useUserRole();

  if (auth.isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div>Loading...</div>
          <div className="loading-subtitle">Initializing application</div>
        </div>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Authentication Error</h2>
          <p>{auth.error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // If authenticated, show authenticated routes, otherwise show public routes
  return (
    <Routes>
      {/* Home route that redirects to flight-ranges if already authenticated */}
      <Route 
        path="/" 
        element={isAuthenticated ? <Navigate to="/flight-ranges" replace /> : <Home />} 
      />

      {/* Admin routes */}
      <Route
        path="/flight-ranges/admin"
        element={
          <ProtectedRoute requireAdmin redirectPath="/flight-ranges">
            <AuthenticatedLayout requireAdmin>
              <FlightRangeAdmin />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route 
        path="/flight-ranges/admin/modify"
        element={
          <ProtectedRoute requireAdmin redirectPath="/flight-ranges">
            <AuthenticatedLayout requireAdmin>
              <FlightRangeModify />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/flight-ranges/admin/partners"
        element={
          <ProtectedRoute requireAdmin redirectPath="/flight-ranges">
            <AuthenticatedLayout requireAdmin>
              <FlightRangeAdmin />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/flight-ranges/admin/history"
        element={
          <ProtectedRoute requireAdmin redirectPath="/flight-ranges">
            <AuthenticatedLayout requireAdmin>
              <FlightRangeAdmin />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/flight-ranges/admin/drafts"
        element={
          <ProtectedRoute requireAdmin redirectPath="/flight-ranges">
            <AuthenticatedLayout requireAdmin>
              <FlightRangeAdmin />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Standard authenticated routes */}
      <Route
        path="/flight-ranges/*"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FlightRange />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/partners/*"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <FlightRange />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default App;
